// Represent absence, never substitute for it.
//
// Every derivation here returns `number | null`. `null` means "we do not know",
// and it is contagious — one unknown input makes the result unknown. Only fmt()
// turns a value into something a user reads, and only at the render boundary.
//
// Why not return the em-dash from the derivations: several call sites feed their
// results back into arithmetic (barchart.js does Math.max over rawData, pointdiff
// builds a Recharts domain with Math.min/Math.max). A string there produces NaN
// and a chart with no axis. So the display string is produced last, never early.

export const EM_DASH = '—';

// The guard the whole module is built on. Deliberately NOT isNaN(): isNaN(null)
// is false, because null coerces to 0. That single coercion is why the old
// calculateStars(null, -5) returned 1 — a one-star recommendation manufactured
// out of missing data.
export function isNum(value) {
  return typeof value === 'number' && Number.isFinite(value);
}

// The only function that returns a display string.
export function fmt(value, digits = 1, { signed = false, suffix = '' } = {}) {
  if (!isNum(value)) return EM_DASH;
  const sign = signed && value > 0 ? '+' : '';
  return `${sign}${value.toFixed(digits)}${suffix}`;
}

// Additive combination. `null + null` is not 0.
export function sum(...values) {
  let total = 0;
  for (const value of values) {
    if (!isNum(value)) return null;
    total += value;
  }
  return total;
}

// a - b. Two unknown point totals are not a dead-even matchup.
export function diff(a, b) {
  if (!isNum(a) || !isNum(b)) return null;
  return a - b;
}

// Mean of the supplied values, unknown if any of them is.
export function mean(...values) {
  const total = sum(...values);
  return total === null ? null : total / values.length;
}

// (numerator / denominator) * 100.
// A zero denominator is unknown, not 0% and not Infinity. A team that has played
// no games does not have a 0% win rate — the strongest negative signal available.
export function ratio(numerator, denominator) {
  if (!isNum(numerator) || !isNum(denominator) || denominator === 0) return null;
  return (numerator / denominator) * 100;
}

// The one true win percentage. Replaces three near-identical copies in wins.js,
// spreads.js and headtohead.js; the headtohead copy was missing the zero-total
// guard entirely and could return Infinity, which rendered as "Infinity%".
export function winPct(wins, draws, total) {
  if (!isNum(wins) || !isNum(draws) || !isNum(total) || total === 0) return null;
  return ((wins + draws * 0.5) / total) * 100;
}

// ---------------------------------------------------------------------------
// The Partial state — data-model.md §10: "a rate over fewer observations than
// the window, rendered with its real denominator (3 of 10)".
//
// Every windowed aggregate in `recent` and `head_to_head` now ships a
// `*_sample_*` companion counting how many of the window's games the value
// could actually be computed from — see librarian hydration.py:284-345. The
// call sites used to pass the window *constant*, so a 2-of-10 cover rate
// computed over the 3 games that had a line was indistinguishable from 2-of-10
// over 10, and a newly promoted club with no history at all rendered 0.0%.
// ---------------------------------------------------------------------------

// The fallback rule, and it is load-bearing.
//
// `sample == null` deliberately, NOT a falsy test. A pre-v3 blob carries no
// companion key at all, and there the window IS what the rate always meant —
// so an absent sample falls back to it and nothing about those fixtures moves.
// A sample of 0 is a fact rather than an absence: it says we observed nothing,
// and ratio/winPct turn a zero denominator into null, which renders unknown.
export function denominator(sample, window) {
  return sample == null ? window : sample;
}

// The two rate derivations, over the real denominator. Both stay number | null
// because the charts feed them to Math.max and a Recharts domain.
export function rateValue(numerator, sample, window) {
  return ratio(numerator, denominator(sample, window));
}

export function winRateValue(wins, draws, sample, window) {
  return winPct(wins, draws, denominator(sample, window));
}

// Shared render boundary for both. A short sample carries its denominator; a
// full or unknown one reads as it always has.
function rateText(value, sample, window) {
  if (value === null) return EM_DASH;
  const text = fmt(value, 1, { suffix: '%' });
  return isNum(sample) && isNum(window) && sample < window
    ? `${text} (${sample}/${window})`
    : text;
}

export function rate(numerator, sample, window) {
  return rateText(rateValue(numerator, sample, window), sample, window);
}

export function winRate(wins, draws, sample, window) {
  return rateText(winRateValue(wins, draws, sample, window), sample, window);
}

// The hit-miss record behind a rate. Replaces the local copy in overs.js and
// the `diff(WINDOW, hits)` idiom in spreads.js and headtohead.js, which reused
// the window literal as the denominator — so changing only the percentage cell
// would have left the two modes of the same cell disagreeing.
export function record(hits, sample, window) {
  const total = denominator(sample, window);
  if (!isNum(hits) || !isNum(total) || total === 0) return EM_DASH;
  return `${fmt(hits, 0)} - ${fmt(total - hits, 0)}`;
}

// Star ratings. Two rules exist in the codebase and both are preserved:
//   'edge' — wins/spreads/headtohead, scored on the gap between two percentages
//   'ev'   — projections, scored on expected value against the market
// Returns null when either input is unknown, so "no data" and "no edge" stay
// distinguishable. StarRating renders nothing for either, but only one of them
// is a claim about the fixture.
export function stars(a, b, variant = 'edge') {
  if (!isNum(a) || !isNum(b)) return null;
  if (variant === 'ev') {
    if (a < 0 && b < 0) return Math.abs(a) < Math.abs(b) ? 1 : 0;
    if (a > 0 && a <= 3) return 2;
    if (a > 3) return 3;
    return 0;
  }
  const gap = a - b;
  if (gap <= 0) return 0;
  if (gap <= 10) return 1;
  if (gap <= 30) return 2;
  return 3;
}

// Tri-state for the ten-box result strips. `undefined` used to take the "miss"
// branch, so a team with no history rendered as ten losses.
export function resultState(value) {
  if (value === true) return 'hit';
  if (value === false) return 'miss';
  return 'unknown';
}

// American odds -> implied probability, as a percentage. Null for an unpriced
// market: the old version fell through to the negative branch, where
// Math.abs(null) is 0, and returned "0.00" — a market asserting a 0% chance.
export function oddsToProb(odds) {
  if (!isNum(odds) || odds === 0) return null;
  const implied = odds > 0 ? 100 / (odds + 100) : Math.abs(odds) / (Math.abs(odds) + 100);
  return implied * 100;
}

// Overround on a two-way market. Unknown if either side is unpriced — the old
// version produced -50.00%, a bookmaker paying out more than it takes in.
export function vig(probA, probB) {
  if (!isNum(probA) || !isNum(probB)) return null;
  return (probA + probB - 100) / 2;
}

// Numeric arrays bound for Recharts or Math.max. Non-numbers become null, which
// Recharts renders as a gap and excludes from the domain; 0 would draw a real
// data point at the origin.
export function chartValues(values) {
  return values.map((value) => (isNum(value) ? value : null));
}

// Symmetric axis bound for the horizontal bar charts. Returns null when nothing
// is known, which is the caller's signal to render a no-data block instead of a
// chart. Guarding this is not cosmetic: an Infinity here fed a
// `for (t = 0; t <= max; t += 10)` loop that never terminates.
export function axisBound(values, pad = 10) {
  const known = values.filter(isNum);
  if (known.length === 0) return null;
  return Math.ceil(Math.max(...known.map(Math.abs)) + pad);
}
